<template>
  <div class="currency-selector flex items-stretch gap-x-1.5">
    <VcDropdownMenu
      :disabled="!!punchoutCookies || isChannelPartner"
      placement="top-start"
      width="100%"
      max-height="h-auto"
      class="relative w-full"
      :class="{ 'open-content': open }"
      :y-offset="-1"
      @toggle="toggle($event)"
    >
      <template #trigger>
        <div
          class="flex h-10 w-full flex-wrap content-between items-center gap-1 rounded-md border border-[color:var(--color-neutral-a3)] bg-white p-[10px] text-sm leading-[1.3] text-[color:var(--color-footer-top-text)] [&_svg]:stroke-[white] [&_svg]:stroke-2"
          :class="{ 'rounded-t-none': open }"
        >
          <div class="flex flex-1 items-center gap-2 font-bold">
            <span class="flex size-5 shrink-0 justify-center text-sm text-[color:var(--color-footer-top-text)]">{{
              currentCurrency?.symbol
            }}</span>
            <span>{{ currentCurrency?.code }}</span>
          </div>

          <VcIcon
            class="shrink-0 text-[--color-footer-top-text]"
            size="xs"
            :name="open ? 'chevron-up' : 'chevron-down'"
          />
        </div>
      </template>

      <template #content>
        <VcMenuItem
          v-for="item in selectableCurrencies"
          :key="item.code"
          :active="item.code === currentCurrency?.code"
          color="neutral"
          @click="select(item.code)"
        >
          <div class="flex items-center gap-2">
            <span
              class="flex size-5 shrink-0 items-center justify-center text-sm text-[color:var(--color-footer-top-text)]"
            >
              {{ item.symbol }}
            </span>
            <span>{{ item.code }}</span>
          </div>
        </VcMenuItem>
      </template>
    </VcDropdownMenu>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute } from "vue-router";
import { changeCartCurrency, changeCartCurrencyForSavedShoppingCarts } from "@/core/api/graphql";
import { updateLanguageAndCurrency } from "@/core/api/graphql/account/mutations/updateLanguageAndCurrency";
import { useCurrency } from "@/core/composables";
import { globals } from "@/core/globals";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useChannelPartner } from "@/shared/account/composables/useChannelPartner";
import { useUser } from "@/shared/account/composables/useUser";
import { useFullCart } from "@/shared/cart";
import {
  CHECKOUT_STEPS_AFTER_SHIPPING_ADDRESS,
  INDIA_INR_REPRICING_FLAG,
  INDIA_SUPPORTED_CURRENCIES,
} from "@/shared/checkout/constants";
import { useWishlists } from "@/shared/wishlists";
import type { InputUpdateLanguageAndCurrencyType } from "@/core/api/graphql/types";

const { currentCurrency, supportedCurrencies, selectableCurrencies, saveCurrencyCode } = useCurrency();
const { isChannelPartner } = useChannelPartner();
const { user } = useUser();
const { cart, shipment } = useFullCart();
const { list } = useWishlists();
const route = useRoute();

const open = ref(false);

function toggle(value: boolean) {
  open.value = value;
}

async function select(code: string) {
  const currency = supportedCurrencies.value.find((x) => x.code === code);
  const languageAndCurrencyInput: InputUpdateLanguageAndCurrencyType = {
    userId: user.value?.id,
    language: user.value?.contact?.id ? user.value?.contact.defaultLanguage : globals.cultureName,
    currency: currency?.code,
  };

  if (currency) {
    localStorage.setItem("previousCurrency", globals.currencyCode);

    await updateLanguageAndCurrency(languageAndCurrencyInput);
    if (cart.value) {
      await changeCartCurrency(cart.value.id, currency.code);
    }
    if (list.value) {
      await changeCartCurrencyForSavedShoppingCarts(list.value.id, currency.code, list.value.name);
    }
  }

  if (currentCurrency.value.code !== code) {
    // Switching away from a currency supported for India (INR or EUR) while past the shipping step
    // with an India delivery address makes the re-priced cart un-checkout-able; flag it so the cart
    // shows the re-pricing notice after reload.
    if (
      !INDIA_SUPPORTED_CURRENCIES.includes(code) &&
      shipment.value?.deliveryAddress?.countryCode === "IND" &&
      CHECKOUT_STEPS_AFTER_SHIPPING_ADDRESS.includes(route.name as string)
    ) {
      sessionStorage.setItem(INDIA_INR_REPRICING_FLAG, "1");
    }

    saveCurrencyCode(code);
  }
}
</script>

<style lang="scss">
.vc-dropdown-menu__list > :not([hidden]) ~ :not([hidden]) {
  border-color: var(--color-neutral-a3) !important;
}
.vc-menu-item__inner:not(:disabled),
.vc-menu-item__inner--color--neutral:hover,
.vc-menu-item__inner--color--neutral:focus {
  color: var(--color-neutral-a2) !important;
}
.vc-dropdown-menu__list {
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4) !important;
}
.vc-menu-item {
  &__inner {
    @apply p-[10px] grid gap-1;
    &--color--neutral {
      &.vc-menu-item__inner--active {
        @apply bg-[--color-neutral-a5];
      }

      &:hover,
      &:focus {
        @apply bg-[--color-neutral-a5] outline-none;
      }
    }
    &.vc-menu-item__inner--size--md {
      @apply px-[10px] py-[9px];
    }
  }
}
.open-content {
  .vc-dropdown-menu__list {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}
.currency-selector [id^="popover-"],
.currency-selector [id*=" popover-"] {
  inset: auto 0px 0px auto !important;
  transform: translate3d(-0.5px, -39px, 0px) !important;
}
</style>
